import { BackToTop } from '@/components/ui/BackToTop';
import {
  TBlogArticleItemized,
  TBlogArticleModule,
  TBlogModuleArticleSection,
  TBlogModuleHero,
} from '../../blog/blog.types';
import { BlogArticleModuleHero } from '../BlogArticleModuleHero';
import { BlogArticlePageOwnership } from '../BlogArticlePageOwnership';
import { BlogArticlePageTableOfContents } from '../BlogArticlePageTableOfContents';
import { BlogArticleSocialShareWidget } from '../BlogArticleSocialShareWidget';
import { BlogArticleWidget } from '../BlogArticleWidget/BlogArticleWidget';
import { BlogPagesShowOnDesktop } from '../BlogPagesShowOnDesktop';
import { BlogPagesShowOnMobile } from '../BlogPagesShowOnMobile';
import { BlogArticleModule } from './BlogArticleModule';

import './BlogArticlePageContent.scss';

type Props = {
  article: TBlogArticleItemized;
};

const isHeroModule = (
  module: undefined | TBlogArticleModule
): module is TBlogModuleHero =>
  module !== undefined && module.__typename === 'blogModuleHero';

export const isBlogArticleSectionModule = (
  module: undefined | TBlogArticleModule
): module is TBlogModuleArticleSection =>
  module !== undefined && module.__typename === 'blogModuleArticleSection';

export const BlogArticlePageContent: React.FC<Props> = ({ article }) => {
  const heroModules = (() => {
    const foundModules = article.item.modules
      .map(({ item }) => item)
      .filter(item => isHeroModule(item));

    return foundModules;
  })();

  const desktopWidgets = (
    <>
      {article.item.widgets.map(widget => (
        <BlogArticleWidget key={widget.item.widgetId} widget={widget.item} />
      ))}

      <BlogArticleSocialShareWidget articleSlug={article.item.articleSlug} />
    </>
  );

  return (
    <>
      <BlogPagesShowOnDesktop>
        <div className='BlogArticlePageContent--desktop'>
          {heroModules.map(heroModule => (
            <BlogArticleModuleHero
              key={heroModule.moduleId}
              module={heroModule}
            />
          ))}

          <div className='BlogArticlePageContent__lower-page--desktop'>
            <div className='BlogArticlePageContent__lower-modules--desktop'>
              <BlogArticlePageOwnership article={article.item} />

              <div
                className='BlogArticlePageContent__article-content--desktop'
                dangerouslySetInnerHTML={{
                  __html: article.item.articleContent,
                }}
              />

              <BlogArticlePageTableOfContents article={article.item} />

              {article.item.modules.map(module => (
                <BlogArticleModule
                  key={module.item.moduleId}
                  module={module.item}
                />
              ))}

              <div className='BlogArticlePageContent__widgets--desktop-below'>
                {desktopWidgets}
              </div>

              <div className='BlogArticlePageContent__back-to-top-button-wrapper'>
                <BackToTop className='BlogArticlePageContent__back-to-top BlogArticlePageContent__back-to-top--desktop' />
              </div>
            </div>

            <div className='BlogArticlePageContent__widgets--desktop-right'>
              {desktopWidgets}
            </div>
          </div>
        </div>
      </BlogPagesShowOnDesktop>

      <BlogPagesShowOnMobile>
        {heroModules.map(heroModule => (
          <div
            className='BlogArticlePageContent--mobile'
            key={heroModule.moduleId}
          >
            <BlogArticleModuleHero module={heroModule} mobile />
          </div>
        ))}

        <div className='BlogArticlePageContent__lower-page--mobile'>
          <div className='BlogArticlePageContent__lower-modules--mobile'>
            <BlogArticlePageOwnership article={article.item} mobile />

            <div
              className='BlogArticlePageContent__article-content--mobile'
              dangerouslySetInnerHTML={{
                __html: article.item.articleContent,
              }}
            />

            <BlogArticlePageTableOfContents
              article={article.item}
              mobile={true}
            />

            {article.item.modules.map(module => (
              <BlogArticleModule
                key={module.item.moduleId}
                module={module.item}
                mobile
              />
            ))}

            <div className='BlogArticlePageContent__widgets--mobile'>
              {article.item.widgets.map(widget => (
                <BlogArticleWidget
                  key={widget.item.widgetId}
                  widget={widget.item}
                  mobile
                />
              ))}

              <BlogArticleSocialShareWidget
                mobile
                articleSlug={article.item.articleSlug}
              />
            </div>

            <div className='BlogArticlePageContent__back-to-top-button-wrapper'>
              <BackToTop className='BlogArticlePageContent__back-to-top BlogArticlePageContent__back-to-top--mobile' />
            </div>
          </div>
        </div>
      </BlogPagesShowOnMobile>
    </>
  );
};
